import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

// Translations
const translations = {
  en: {
    // Common
    welcome: "Welcome",
    dashboard: "Dashboard",
    logout: "Logout",
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    phone: "Phone",
    name: "Name",
    fullName: "Full Name",
    address: "Address",
    search: "Search",
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    loading: "Loading...",
    noData: "No data available",
    status: "Status",
    date: "Date",
    total: "Total",
    actions: "Actions",
    details: "Details",
    view: "View",
    edit: "Edit",
    delete: "Delete",
    back: "Back",
    backToHome: "Back to Home",
    auto: "Auto",
    deliveryLocation: "Delivery Location",
    items: "items",
    each: "each",
    outOfStock: "Out of Stock",
    overview: "Overview",
    all: "All",
    allOrders: "All Orders",
    recentOrders: "Recent Orders",
    viewAll: "View All",
    noShopsYet: "No shops yet",
    createFirstShop: "Create your first shop to start selling",
    noProductsYet: "No products yet",
    addFirstProduct: "Add your first product to start selling",
    noOrdersYet: "No orders found",
    startShoppingNow: "Start shopping to see your orders here",
    noActiveDeliveries: "No active deliveries",
    waitForOrders: "Wait for new orders to be assigned to you",
    turnOnAvailability: "Turn on availability to receive new orders",
    deliveryHistory: "Your delivery history will appear here",
    orderId: "Order ID",
    selectShop: "Select Shop",
    
    // Navigation
    home: "Home",
    shops: "Shops",
    orders: "Orders",
    products: "Products",
    reviews: "Reviews",
    settings: "Settings",
    deliverTo: "Deliver to",
    yourLocation: "Your Location",
    searchPlaceholder: "Search for shops or products...",
    
    // Landing Page
    heroTitle: "Village Shop,",
    heroSubtitle: "Home Delivery!",
    heroDescription: "Order fresh groceries, electronics, fashion from your favorite local shops.",
    startShopping: "Start Shopping",
    joinCommunity: "Join Community",
    exploreCategories: "Explore Categories",
    featuredShops: "Featured Shops",
    customerReviews: "What Our Customers Say",
    getExclusiveOffers: "Get Exclusive Offers from Local Shops!",
    subscribe: "Subscribe",
    enterEmail: "Enter your email",
    findWhatYouNeed: "Find exactly what you need from local shops",
    handpickedStores: "Handpicked local stores with the best quality products",
    shopsFound: "Shops Found",
    realPhotosRealReviews: "Real photos, real reviews from happy customers",
    fastestDelivery: "Fastest delivery in your area",
    fastDelivery: "Fast Delivery",
    under30mins: "Under 30 mins",
    verifiedShops: "Verified Shops",
    securePayments: "Secure Payments",
    howItWorks: "How It Works",
    simpleEasyFast: "Simple. Easy. Fast.",
    just3Steps: "Just 3 Steps!",
    browseShopsStep: "Browse Shops",
    findLocalShops: "Find local shops near you and browse their fresh products",
    placeOrderStep: "Place Order",
    addItemsCheckout: "Add items to cart and checkout with cash on delivery",
    getDeliveredStep: "Get Delivered",
    trackReceive: "Track your order and receive it at your doorstep",
    readyToStart: "Ready to Get Started?",
    joinThousands: "Join thousands of users enjoying local shopping made easy",
    thousandsFamilies: "Thousands of families already joined!",
    signUpFreeToday: "Sign Up Free Today",
    quickLinks: "Quick Links",
    aboutUs: "About Us",
    becomePartner: "Become a Partner",
    careers: "Careers",
    support: "Support",
    helpCenter: "Help Center",
    contactUs: "Contact Us",
    faqs: "FAQs",
    legal: "Legal",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    refundPolicy: "Refund Policy",
    allRightsReserved: "All rights reserved.",
    localFreshFast: "Local. Fresh. Fast.",
    favoriteLocalShops: "Your favorite local shops, delivered to your doorstep.",
    peopleOrderedRecently: "people ordered recently",
    popular: "Popular",
    
    // Categories
    groceries: "Groceries",
    electronics: "Electronics",
    fashion: "Fashion",
    homeKitchen: "Home & Kitchen",
    pharmacy: "Pharmacy",
    books: "Books",
    localShop: "Local Shop",
    
    // Auth
    welcomeBack: "Welcome Back!",
    createAccount: "Create Account",
    loginToContinue: "Login to continue shopping",
    createAccountToStart: "Create your account to get started",
    iAmA: "I am a",
    customer: "Customer",
    shopOwner: "Shop Owner",
    deliveryAgent: "Delivery Agent",
    orderFromShops: "Order from local shops",
    manageYourShop: "Manage your shop",
    deliverOrders: "Deliver orders",
    
    // Customer Dashboard
    customerDashboard: "Customer Dashboard",
    browseShops: "Browse shops and order your daily essentials",
    myOrders: "My Orders",
    orderHistory: "Order History",
    trackOrder: "Track Order",
    reorder: "Reorder",
    writeReview: "Write Review",
    viewShop: "View Shop",
    addToCart: "Add to Cart",
    addedToCart: "added to cart",
    cart: "Cart",
    cartEmpty: "Your cart is empty",
    checkout: "Checkout",
    placeOrder: "Place Order",
    orderPlaced: "Order Placed Successfully!",
    deliveryAddress: "Delivery Address",
    paymentMethod: "Payment Method",
    cashOnDelivery: "Cash on Delivery",
    orderTotal: "Order Total",
    estimatedDelivery: "Estimated Delivery",
    totalSpent: "Total Spent",
    
    // Shop Owner Dashboard
    shopOwnerDashboard: "Shop Owner Dashboard",
    manageShopsProducts: "Manage your shops, products, and orders",
    totalShops: "Total Shops",
    totalProducts: "Total Products",
    totalOrders: "Total Orders",
    pendingOrders: "Pending Orders",
    todaysOrders: "Today's Orders",
    revenue: "Revenue",
    createShop: "Create Shop",
    addShop: "Add Shop",
    addProduct: "Add Product",
    shopName: "Shop Name",
    shopDescription: "Shop Description",
    productName: "Product Name",
    productDescription: "Product Description",
    price: "Price",
    stock: "Stock",
    category: "Category",
    assignDelivery: "Assign Delivery",
    selectAgent: "Select Delivery Agent",
    noAgentsAvailable: "No delivery agents available",
    orderDetails: "Order Details",
    customerName: "Customer Name",
    customerPhone: "Customer Phone",
    orderItems: "Order Items",
    describeShop: "Describe your shop",
    fullAddress: "Full address",
    productCategory: "Groceries, Electronics, etc.",
    
    // Delivery Agent Dashboard
    deliveryDashboard: "Delivery Dashboard",
    manageDeliveries: "Manage your deliveries and availability",
    availability: "Availability",
    available: "Available",
    notAvailable: "Not Available",
    toggleAvailability: "Toggle Availability",
    canReceiveOrders: "You can receive new orders",
    cannotReceiveOrders: "You will not receive new orders",
    activeDeliveries: "Active Deliveries",
    completedDeliveries: "Completed Deliveries",
    pendingDeliveries: "Pending Deliveries",
    pickupAddress: "Pickup Address",
    deliveryAddressLabel: "Delivery Address",
    shopLocation: "Shop Location",
    updateStatus: "Update Status",
    markPickedUp: "Mark as Picked Up",
    markDelivered: "Mark as Delivered",
    earnings: "Earnings",
    todaysEarnings: "Today's Earnings",
    today: "Today",
    
    // Order Status
    pending: "Pending",
    assigned: "Assigned",
    pickedUp: "Picked Up",
    inTransit: "In Transit",
    delivered: "Delivered",
    cancelled: "Cancelled",
    
    // Shop Detail
    customerRatings: "Customer Ratings",
    noReviewsYet: "No reviews yet",
    beFirstToReview: "Be the first to review this shop!",
    writeFirstReview: "Write First Review",
    yourRating: "Your Rating",
    yourReview: "Your Review",
    shareExperience: "Share your experience with this shop... What did you like? How was the service?",
    submitReview: "Submit Review",
    poor: "Poor",
    fair: "Fair",
    good: "Good",
    veryGood: "Very Good",
    excellent: "Excellent",
    orderNow: "Order Now",
    orderFrom: "Order from",
    shopNotFound: "Shop not found",
    
    // Messages
    orderSuccess: "Order placed successfully!",
    reviewSuccess: "Review submitted successfully!",
    shopCreated: "Shop created successfully!",
    productAdded: "Product added successfully!",
    agentAssigned: "Delivery agent assigned!",
    statusUpdated: "Status updated successfully!",
    availabilityUpdated: "Availability updated!",
    
    // Errors
    loginFailed: "Login failed",
    registerFailed: "Registration failed",
    orderFailed: "Failed to place order",
    loadFailed: "Failed to load data",
    pleaseLogin: "Please login to write a review",
    pleaseSetLocation: "Please set your delivery location",
    cartIsEmpty: "Cart is empty",
    pleaseWriteReview: "Please write a review comment",
    deliveryTime: "20-30 min delivery",
    submitting: "Submitting...",
    noProductsAvailable: "No products available",
    goToDashboard: "Go to Dashboard",
    anonymous: "Anonymous",
  },
  hi: {
    // Common
    welcome: "स्वागत है",
    dashboard: "डैशबोर्ड",
    logout: "लॉग आउट",
    login: "लॉग इन",
    register: "रजिस्टर",
    email: "ईमेल",
    password: "पासवर्ड",
    phone: "फ़ोन",
    name: "नाम",
    fullName: "पूरा नाम",
    address: "पता",
    search: "खोजें",
    save: "सेव करें",
    cancel: "रद्द करें",
    submit: "जमा करें",
    loading: "लोड हो रहा है...",
    noData: "कोई डेटा उपलब्ध नहीं",
    status: "स्थिति",
    date: "तारीख",
    total: "कुल",
    actions: "कार्रवाई",
    details: "विवरण",
    view: "देखें",
    edit: "संपादित करें",
    delete: "हटाएं",
    back: "वापस",
    backToHome: "होम पर वापस जाएं",
    auto: "ऑटो",
    deliveryLocation: "डिलीवरी स्थान",
    items: "आइटम",
    each: "प्रति",
    outOfStock: "स्टॉक में नहीं",
    overview: "अवलोकन",
    all: "सभी",
    allOrders: "सभी ऑर्डर",
    recentOrders: "हाल के ऑर्डर",
    viewAll: "सभी देखें",
    noShopsYet: "अभी कोई दुकान नहीं",
    createFirstShop: "बेचना शुरू करने के लिए अपनी पहली दुकान बनाएं",
    noProductsYet: "अभी कोई उत्पाद नहीं",
    addFirstProduct: "बेचना शुरू करने के लिए अपना पहला उत्पाद जोड़ें",
    noOrdersYet: "कोई ऑर्डर नहीं मिला",
    startShoppingNow: "अपने ऑर्डर यहाँ देखने के लिए खरीदारी शुरू करें",
    noActiveDeliveries: "कोई सक्रिय डिलीवरी नहीं",
    waitForOrders: "नए ऑर्डर असाइन होने की प्रतीक्षा करें",
    turnOnAvailability: "नए ऑर्डर प्राप्त करने के लिए उपलब्धता चालू करें",
    deliveryHistory: "आपका डिलीवरी इतिहास यहाँ दिखेगा",
    orderId: "ऑर्डर आईडी",
    selectShop: "दुकान चुनें",
    
    // Navigation
    home: "होम",
    shops: "दुकानें",
    orders: "ऑर्डर",
    products: "उत्पाद",
    reviews: "समीक्षाएं",
    settings: "सेटिंग्स",
    deliverTo: "डिलीवरी का पता",
    yourLocation: "आपका स्थान",
    searchPlaceholder: "दुकान या उत्पाद खोजें...",
    
    // Landing Page
    heroTitle: "गांव की दुकान,",
    heroSubtitle: "घर का सामान!",
    heroDescription: "अपनी पसंदीदा लोकल दुकानों से ताज़ा किराना, इलेक्ट्रॉनिक्स, फैशन मंगवाएं।",
    startShopping: "खरीदारी शुरू करें",
    joinCommunity: "समुदाय से जुड़ें",
    exploreCategories: "श्रेणियां देखें",
    featuredShops: "विशेष दुकानें",
    customerReviews: "ग्राहक क्या कहते हैं",
    getExclusiveOffers: "लोकल दुकानों से विशेष ऑफर पाएं!",
    subscribe: "सब्सक्राइब करें",
    enterEmail: "अपना ईमेल दर्ज करें",
    findWhatYouNeed: "लोकल दुकानों से जो चाहिए वो पाएं",
    handpickedStores: "सर्वश्रेष्ठ गुणवत्ता वाली चुनिंदा लोकल दुकानें",
    shopsFound: "दुकानें मिलीं",
    realPhotosRealReviews: "खुश ग्राहकों की असली तस्वीरें और समीक्षाएं",
    fastestDelivery: "आपके क्षेत्र में सबसे तेज़ डिलीवरी",
    fastDelivery: "तेज़ डिलीवरी",
    under30mins: "30 मिनट के अंदर",
    verifiedShops: "सत्यापित दुकानें",
    securePayments: "सुरक्षित भुगतान",
    howItWorks: "कैसे काम करता है",
    simpleEasyFast: "सरल। आसान। तेज़।",
    just3Steps: "सिर्फ 3 स्टेप में!",
    browseShopsStep: "दुकानें देखें",
    findLocalShops: "अपने पास की लोकल दुकानें खोजें और ताज़ा उत्पाद देखें",
    placeOrderStep: "ऑर्डर करें",
    addItemsCheckout: "कार्ट में सामान डालें और कैश ऑन डिलीवरी से भुगतान करें",
    getDeliveredStep: "डिलीवरी पाएं",
    trackReceive: "अपना ऑर्डर ट्रैक करें और घर पर प्राप्त करें",
    readyToStart: "शुरू करने के लिए तैयार हैं?",
    joinThousands: "हजारों यूज़र्स के साथ आसान लोकल शॉपिंग का आनंद लें",
    thousandsFamilies: "हजारों परिवार पहले से जुड़े हैं!",
    signUpFreeToday: "आज ही मुफ्त साइन अप करें",
    quickLinks: "त्वरित लिंक",
    aboutUs: "हमारे बारे में",
    becomePartner: "पार्टनर बनें",
    careers: "करियर",
    support: "सहायता",
    helpCenter: "सहायता केंद्र",
    contactUs: "संपर्क करें",
    faqs: "अक्सर पूछे जाने वाले प्रश्न",
    legal: "कानूनी",
    privacyPolicy: "गोपनीयता नीति",
    termsOfService: "सेवा की शर्तें",
    refundPolicy: "रिफंड नीति",
    allRightsReserved: "सर्वाधिकार सुरक्षित।",
    localFreshFast: "लोकल। ताज़ा। तेज़।",
    favoriteLocalShops: "आपकी पसंदीदा लोकल दुकानें, सीधे आपके घर।",
    peopleOrderedRecently: "लोगों ने हाल ही में ऑर्डर किया",
    popular: "लोकप्रिय",
    
    // Categories
    groceries: "किराना",
    electronics: "इलेक्ट्रॉनिक्स",
    fashion: "फैशन",
    homeKitchen: "घर और रसोई",
    pharmacy: "दवाई",
    books: "किताबें",
    localShop: "लोकल दुकान",
    
    // Auth
    welcomeBack: "वापसी पर स्वागत है!",
    createAccount: "खाता बनाएं",
    loginToContinue: "खरीदारी जारी रखने के लिए लॉगिन करें",
    createAccountToStart: "शुरू करने के लिए अपना खाता बनाएं",
    iAmA: "मैं हूं",
    customer: "ग्राहक",
    shopOwner: "दुकान मालिक",
    deliveryAgent: "डिलीवरी एजेंट",
    orderFromShops: "लोकल दुकानों से ऑर्डर करें",
    manageYourShop: "अपनी दुकान प्रबंधित करें",
    deliverOrders: "ऑर्डर डिलीवर करें",
    
    // Customer Dashboard
    customerDashboard: "ग्राहक डैशबोर्ड",
    browseShops: "दुकानें देखें और रोज़मर्रा का सामान ऑर्डर करें",
    myOrders: "मेरे ऑर्डर",
    orderHistory: "ऑर्डर इतिहास",
    trackOrder: "ऑर्डर ट्रैक करें",
    reorder: "फिर से ऑर्डर करें",
    writeReview: "समीक्षा लिखें",
    viewShop: "दुकान देखें",
    addToCart: "कार्ट में डालें",
    addedToCart: "कार्ट में जोड़ा गया",
    cart: "कार्ट",
    cartEmpty: "आपका कार्ट खाली है",
    checkout: "चेकआउट",
    placeOrder: "ऑर्डर करें",
    orderPlaced: "ऑर्डर सफलतापूर्वक हो गया!",
    deliveryAddress: "डिलीवरी पता",
    paymentMethod: "भुगतान का तरीका",
    cashOnDelivery: "कैश ऑन डिलीवरी",
    orderTotal: "कुल राशि",
    estimatedDelivery: "अनुमानित डिलीवरी",
    totalSpent: "कुल खर्च",
    
    // Shop Owner Dashboard
    shopOwnerDashboard: "दुकान मालिक डैशबोर्ड",
    manageShopsProducts: "अपनी दुकानें, उत्पाद और ऑर्डर प्रबंधित करें",
    totalShops: "कुल दुकानें",
    totalProducts: "कुल उत्पाद",
    totalOrders: "कुल ऑर्डर",
    pendingOrders: "लंबित ऑर्डर",
    todaysOrders: "आज के ऑर्डर",
    revenue: "आय",
    createShop: "दुकान बनाएं",
    addShop: "दुकान जोड़ें",
    addProduct: "उत्पाद जोड़ें",
    shopName: "दुकान का नाम",
    shopDescription: "दुकान का विवरण",
    productName: "उत्पाद का नाम",
    productDescription: "उत्पाद का विवरण",
    price: "कीमत",
    stock: "स्टॉक",
    category: "श्रेणी",
    assignDelivery: "डिलीवरी असाइन करें",
    selectAgent: "डिलीवरी एजेंट चुनें",
    noAgentsAvailable: "कोई डिलीवरी एजेंट उपलब्ध नहीं",
    orderDetails: "ऑर्डर विवरण",
    customerName: "ग्राहक का नाम",
    customerPhone: "ग्राहक का फ़ोन",
    orderItems: "ऑर्डर आइटम",
    describeShop: "अपनी दुकान का विवरण दें",
    fullAddress: "पूरा पता",
    productCategory: "किराना, इलेक्ट्रॉनिक्स, आदि।",
    
    // Delivery Agent Dashboard
    deliveryDashboard: "डिलीवरी डैशबोर्ड",
    manageDeliveries: "अपनी डिलीवरी और उपलब्धता प्रबंधित करें",
    availability: "उपलब्धता",
    available: "उपलब्ध",
    notAvailable: "उपलब्ध नहीं",
    toggleAvailability: "उपलब्धता बदलें",
    canReceiveOrders: "आप नए ऑर्डर प्राप्त कर सकते हैं",
    cannotReceiveOrders: "आप नए ऑर्डर प्राप्त नहीं करेंगे",
    activeDeliveries: "सक्रिय डिलीवरी",
    completedDeliveries: "पूर्ण डिलीवरी",
    pendingDeliveries: "लंबित डिलीवरी",
    pickupAddress: "पिकअप पता",
    deliveryAddressLabel: "डिलीवरी पता",
    shopLocation: "दुकान का स्थान",
    updateStatus: "स्थिति अपडेट करें",
    markPickedUp: "पिक अप के रूप में चिह्नित करें",
    markDelivered: "डिलीवर के रूप में चिह्नित करें",
    earnings: "कमाई",
    todaysEarnings: "आज की कमाई",
    today: "आज",
    
    // Order Status
    pending: "लंबित",
    assigned: "असाइन किया गया",
    pickedUp: "पिक अप किया",
    inTransit: "रास्ते में",
    delivered: "डिलीवर हो गया",
    cancelled: "रद्द",
    
    // Shop Detail
    customerRatings: "ग्राहक रेटिंग",
    noReviewsYet: "अभी कोई समीक्षा नहीं",
    beFirstToReview: "इस दुकान की पहली समीक्षा लिखें!",
    writeFirstReview: "पहली समीक्षा लिखें",
    yourRating: "आपकी रेटिंग",
    yourReview: "आपकी समीक्षा",
    shareExperience: "इस दुकान के साथ अपना अनुभव साझा करें... आपको क्या पसंद आया? सेवा कैसी थी?",
    submitReview: "समीक्षा जमा करें",
    poor: "खराब",
    fair: "ठीक",
    good: "अच्छा",
    veryGood: "बहुत अच्छा",
    excellent: "उत्कृष्ट",
    orderNow: "अभी ऑर्डर करें",
    orderFrom: "से ऑर्डर करें",
    shopNotFound: "दुकान नहीं मिली",
    
    // Messages
    orderSuccess: "ऑर्डर सफलतापूर्वक हो गया!",
    reviewSuccess: "समीक्षा सफलतापूर्वक जमा हुई!",
    shopCreated: "दुकान सफलतापूर्वक बनाई गई!",
    productAdded: "उत्पाद सफलतापूर्वक जोड़ा गया!",
    agentAssigned: "डिलीवरी एजेंट असाइन किया गया!",
    statusUpdated: "स्थिति अपडेट हो गई!",
    availabilityUpdated: "उपलब्धता अपडेट हो गई!",
    
    // Errors
    loginFailed: "लॉगिन विफल",
    registerFailed: "रजिस्ट्रेशन विफल",
    orderFailed: "ऑर्डर विफल",
    loadFailed: "डेटा लोड करने में विफल",
    pleaseLogin: "समीक्षा लिखने के लिए कृपया लॉगिन करें",
    pleaseSetLocation: "कृपया अपना डिलीवरी स्थान सेट करें",
    cartIsEmpty: "कार्ट खाली है",
    pleaseWriteReview: "कृपया समीक्षा टिप्पणी लिखें",
    deliveryTime: "20-30 मिनट डिलीवरी",
    submitting: "जमा हो रहा है...",
    noProductsAvailable: "कोई उत्पाद उपलब्ध नहीं",
    goToDashboard: "डैशबोर्ड पर जाएं",
    anonymous: "अज्ञात",
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('samaandena_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('samaandena_language', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
